import { t } from "../lib/i18n";
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export type RegionKey = 'US' | 'UK' | 'NORDIC_EU';

export const InstantUnderwritingWidget: React.FC = () => {
  const navigate = useNavigate();
  const [region, setRegion] = useState<RegionKey>('US');
  const [currencySymbol, setCurrencySymbol] = useState<string>('$');
  const [nordicCurrency, setNordicCurrency] = useState<'EUR' | 'SEK' | 'NOK' | 'DKK'>('EUR');

  // Core Inputs
  const [purchasePrice, setPurchasePrice] = useState<number>(1500000);
  const [grossRentalIncome, setGrossRentalIncome] = useState<number>(135000);
  const [area, setArea] = useState<number>(10000); // sq ft or m2
  const [vacancyRate, setVacancyRate] = useState<number>(5.0);
  const [operatingExpenses, setOperatingExpenses] = useState<number>(35000);
  const [capex] = useState<number>(5000);
  const [ltvPercent, setLtvPercent] = useState<number>(65);
  const [interestRate, setInterestRate] = useState<number>(5.5);
  const [loanTerm, setLoanTerm] = useState<number>(25);

  // Synchronize currency and unit defaults on region change
  const handleRegionChange = (newRegion: RegionKey) => {
    setRegion(newRegion);
    if (newRegion === 'US') {
      setCurrencySymbol('$');
    } else if (newRegion === 'UK') {
      setCurrencySymbol('£');
    } else {
      updateNordicCurrency(nordicCurrency);
    }
  };

  const updateNordicCurrency = (curr: 'EUR' | 'SEK' | 'NOK' | 'DKK') => {
    setNordicCurrency(curr);
    if (curr === 'EUR') setCurrencySymbol('€');
    else setCurrencySymbol('kr ');
  };

  const areaUnit = region === 'US' ? 'sq ft' : 'm²';
  const yieldTerm = region === 'US' ? 'Cap Rate' : region === 'UK' ? 'Net Initial Yield (NIY)' : 'Net Yield';

  // Apply Presets
  const applyPreset = (type: 'office' | 'industrial' | 'multifamily') => {
    if (type === 'office') {
      setPurchasePrice(2200000);
      setGrossRentalIncome(195000);
      setArea(region === 'US' ? 14000 : 1300);
      setOperatingExpenses(50000);
      setVacancyRate(8);
    } else if (type === 'industrial') {
      setPurchasePrice(3100000);
      setGrossRentalIncome(245000);
      setArea(region === 'US' ? 30000 : 2800);
      setOperatingExpenses(38000);
      setVacancyRate(3);
    } else {
      setPurchasePrice(1250000);
      setGrossRentalIncome(110000);
      setArea(region === 'US' ? 9500 : 880);
      setOperatingExpenses(26000);
      setVacancyRate(4);
    }
  };

  // Computations
  const results = useMemo(() => {
    const effectiveGrossIncome = grossRentalIncome * (1 - vacancyRate / 100);
    const noi = Math.max(0, effectiveGrossIncome - operatingExpenses - capex);
    const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0;
    const grossYield = purchasePrice > 0 ? (grossRentalIncome / purchasePrice) * 100 : 0;
    const rentPerUnitArea = area > 0 ? grossRentalIncome / area : 0;

    const loanAmount = purchasePrice * (ltvPercent / 100);
    const downPayment = purchasePrice - loanAmount;

    // Monthly debt service calculation
    const monthlyRate = (interestRate / 100) / 12;
    const totalPayments = loanTerm * 12;
    const annualDebtService =
      totalPayments > 0 && monthlyRate > 0 && loanAmount > 0
        ? ((loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)) *
          12
        : 0;

    const cashFlow = noi - annualDebtService;
    const cashOnCash = downPayment > 0 ? (cashFlow / downPayment) * 100 : 0;
    const dscr = annualDebtService > 0 ? noi / annualDebtService : 999;

    return {
      noi,
      capRate,
      grossYield,
      rentPerUnitArea,
      loanAmount,
      downPayment,
      annualDebtService,
      cashFlow,
      cashOnCash,
      dscr,
      effectiveGrossIncome,
    };
  }, [purchasePrice, grossRentalIncome, area, vacancyRate, operatingExpenses, capex, ltvPercent, interestRate, loanTerm]);

  // Save deal and transition to app
  const handleSaveAndUnlock = () => {
    const payload = {
      region,
      currencySymbol,
      purchasePrice,
      grossRentalIncome,
      area,
      areaUnit,
      vacancyRate,
      operatingExpenses,
      capex,
      ltvPercent,
      interestRate,
      loanTerm,
      results,
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem('pending_screener_deal', JSON.stringify(payload));
    } catch (e) {
      console.warn('Could not save to localStorage', e);
    }
    navigate('/login');
  };

  return (
    <div id="deal-screener" className="w-full max-w-6xl mx-auto my-8 p-6 md:p-8 bg-white border border-slate-200/90 rounded-2xl shadow-xl text-slate-900">
      {/* Header and Region Selector */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-6 border-b border-slate-200 gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-blue-700 text-xs font-semibold uppercase tracking-wider mb-2">
            <span>{t("instantUnderwritingWidget.k1")}</span>
            <span className="text-slate-300">•</span>
            <span>{t("instantUnderwritingWidget.k2")}</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            {t("instantUnderwritingWidget.k3")} </h2>
          <p className="text-slate-500 text-sm mt-1">
            {t("instantUnderwritingWidget.k4")} </p>
        </div>

        {/* Region Pills */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl border border-slate-200">
          <button
            type="button"
            onClick={() => handleRegionChange('US')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              region === 'US'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
            }`}
          >
            {t("instantUnderwritingWidget.k5")} </button>
          <button
            type="button"
            onClick={() => handleRegionChange('UK')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              region === 'UK'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
            }`}
          >
            {t("instantUnderwritingWidget.k6")} </button>
          <button
            type="button"
            onClick={() => handleRegionChange('NORDIC_EU')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              region === 'NORDIC_EU'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
            }`}
          >
            {t("instantUnderwritingWidget.k7")} </button>
        </div>
      </div>

      {/* Nordic Currency Sub-bar if selected */}
      {region === 'NORDIC_EU' && (
        <div className="flex items-center gap-3 py-2.5 px-4 my-4 bg-slate-50 border border-slate-200 rounded-xl text-xs">
          <span className="text-slate-600 font-semibold">{t("instantUnderwritingWidget.k8")}</span>
          {(['EUR', 'SEK', 'NOK', 'DKK'] as const).map((curr) => (
            <button
              key={curr}
              type="button"
              onClick={() => updateNordicCurrency(curr)}
              className={`px-2.5 py-1 rounded-md transition-all font-semibold ${
                nordicCurrency === curr ? 'bg-blue-100 text-blue-800 border border-blue-300 font-bold' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {curr} ({curr === 'EUR' ? '€' : t("instantUnderwritingWidget.k9")})
            </button>
          ))}
          <span className="text-slate-500 text-[11px] ml-auto hidden sm:inline">
            {t("instantUnderwritingWidget.k10")} </span>
        </div>
      )}

      {/* Preset Buttons */}
      <div className="flex flex-wrap items-center gap-2 pt-4 pb-2">
        <span className="text-xs text-slate-500 font-semibold">{t("instantUnderwritingWidget.k11")}</span>
        <button
          type="button"
          onClick={() => applyPreset('office')}
          className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-md border border-slate-200 transition font-medium"
        >
          {t("instantUnderwritingWidget.k12")} </button>
        <button
          type="button"
          onClick={() => applyPreset('industrial')}
          className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-md border border-slate-200 transition font-medium"
        >
          {t("instantUnderwritingWidget.k13")} </button>
        <button
          type="button"
          onClick={() => applyPreset('multifamily')}
          className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-md border border-slate-200 transition font-medium"
        >
          {t("instantUnderwritingWidget.k14")} </button>
      </div>

      {/* Main Grid: Inputs vs Real-time KPI Dash */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
        {/* Left Inputs (7 cols) */}
        <div className="lg:col-span-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t("instantUnderwritingWidget.k15")}{currencySymbol})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400 text-sm font-semibold">{currencySymbol}</span>
                <input
                  type="number"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(parseFloat(e.target.value) || 0)}
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-600 focus:bg-white focus:ring-1 focus:ring-blue-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t("instantUnderwritingWidget.k16")}{currencySymbol})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400 text-sm font-semibold">{currencySymbol}</span>
                <input
                  type="number"
                  value={grossRentalIncome}
                  onChange={(e) => setGrossRentalIncome(parseFloat(e.target.value) || 0)}
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-600 focus:bg-white focus:ring-1 focus:ring-blue-600"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t("instantUnderwritingWidget.k17")}{areaUnit})
              </label>
              <input
                type="number"
                value={area}
                onChange={(e) => setArea(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-600 focus:bg-white focus:ring-1 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t("instantUnderwritingWidget.k18")} </label>
              <input
                type="number"
                step="0.5"
                value={vacancyRate}
                onChange={(e) => setVacancyRate(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-600 focus:bg-white focus:ring-1 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t("instantUnderwritingWidget.k19")}{currencySymbol})
              </label>
              <input
                type="number"
                value={operatingExpenses}
                onChange={(e) => setOperatingExpenses(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-600 focus:bg-white focus:ring-1 focus:ring-blue-600"
              />
            </div>
          </div>

          {/* Financing Module */}
          <div className="pt-2 border-t border-slate-200">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              {t("instantUnderwritingWidget.k20")} </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1 font-medium">{t("instantUnderwritingWidget.k21")}{ltvPercent}%)</label>
                <input
                  type="range"
                  min="0"
                  max="90"
                  step="5"
                  value={ltvPercent}
                  onChange={(e) => setLtvPercent(parseFloat(e.target.value) || 0)}
                  className="w-full accent-blue-600"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-600 mb-1 font-medium">{t("instantUnderwritingWidget.k22")}{interestRate}%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-xs focus:outline-none focus:border-blue-600 focus:bg-white"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-600 mb-1 font-medium">{t("instantUnderwritingWidget.k23")}{loanTerm} {t("instantUnderwritingWidget.k24")}</label>
                <input
                  type="number"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-xs focus:outline-none focus:border-blue-600 focus:bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Output Results Card (5 cols) */}
        <div className="lg:col-span-5 bg-gradient-to-b from-blue-50/70 via-indigo-50/30 to-white border border-blue-200/90 rounded-xl p-5 flex flex-col justify-between shadow-sm">
          <div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                {t("instantUnderwritingWidget.k25")} </span>
              <span className="px-2 py-0.5 bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-bold rounded">
                {t("instantUnderwritingWidget.k26")} </span>
            </div>

            {/* Primary Yield Metric */}
            <div className="p-4 bg-white border border-blue-200/80 rounded-xl mb-4 shadow-sm">
              <div className="flex justify-between items-end">
                <div>
                  <div className="text-xs font-semibold text-slate-500">{yieldTerm}</div>
                  <div className="text-3xl font-black text-blue-600 mt-0.5">
                    {results.capRate.toFixed(2)}%
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold text-slate-500">{t("instantUnderwritingWidget.k27")}</div>
                  <div className="text-xl font-bold text-emerald-600 mt-0.5">
                    {currencySymbol}
                    {Math.round(results.noi).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {/* Metrics 2x2 Grid */}
            <div className="grid grid-cols-2 gap-3 text-xs mb-4">
              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-2xs">
                <span className="text-slate-500 block font-medium">{t("instantUnderwritingWidget.k28")}</span>
                <span className="text-base font-bold text-slate-900 mt-1 block">
                  {results.cashOnCash.toFixed(2)}%
                </span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-2xs">
                <span className="text-slate-500 block font-medium">{t("instantUnderwritingWidget.k29")}</span>
                <span className={`text-base font-bold mt-1 block ${results.dscr >= 1.25 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {results.dscr > 50 ? t("instantUnderwritingWidget.k30") : `${results.dscr.toFixed(2)}x`}
                </span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-2xs">
                <span className="text-slate-500 block font-medium">{t("instantUnderwritingWidget.k31")}</span>
                <span className="text-base font-bold text-slate-900 mt-1 block">
                  {results.grossYield.toFixed(2)}%
                </span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-2xs">
                <span className="text-slate-500 block font-medium">{t("instantUnderwritingWidget.k32")}</span>
                <span className="text-base font-bold text-slate-900 mt-1 block">
                  {currencySymbol}
                  {Math.round(results.cashFlow).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="text-[11px] text-slate-500 flex justify-between py-1 border-t border-slate-200">
              <span>{t("instantUnderwritingWidget.k33")} {currencySymbol}{results.rentPerUnitArea.toFixed(2)} / {areaUnit} {t("instantUnderwritingWidget.k34")}</span>
              <span>{t("instantUnderwritingWidget.k35")} {currencySymbol}{Math.round(results.downPayment).toLocaleString()}</span>
            </div>
          </div>

          {/* Smart Account Gate CTA */}
          <div className="mt-5 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={handleSaveAndUnlock}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-sm rounded-xl shadow-md transition-all transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
            >
              <span>{t("instantUnderwritingWidget.k36")}</span>
              <span>→</span>
            </button>
            <p className="text-center text-slate-500 text-[11px] mt-2">
              {t("instantUnderwritingWidget.k37")} </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstantUnderwritingWidget;
